<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import MessageReactionPicker from './MessageReactionPicker.vue';
import MessageReactionBadge from './MessageReactionBadge.vue';
import { MESSAGE_TYPES } from './constants';

const props = defineProps({
  messageId: { type: Number, required: true },
  conversationId: { type: Number, required: true },
  messageType: { type: Number, required: true },
  isPrivate: { type: Boolean, default: false },
  createdAt: { type: Number, required: true },
  eligible: { type: Boolean, default: false },
  inbox: { type: Object, required: true },
  contentAttributes: { type: Object, default: () => ({}) },
});
const store = useStore();
const { t } = useI18n();
const pending = ref(false);
const error = ref('');
const noteFailed = ref(false);
const emojis = ['✅', '👍', '❤️', '👀'];
const isPublicIncoming = computed(
  () => props.messageType === MESSAGE_TYPES.INCOMING && !props.isPrivate
);
const canReact = computed(
  () =>
    props.eligible &&
    isPublicIncoming.value &&
    props.inbox.channel_type === 'Channel::Whatsapp' &&
    props.inbox.provider === 'whatsapp_cloud' &&
    !props.contentAttributes.deleted &&
    props.createdAt > Date.now() / 1000 - 30 * 24 * 60 * 60 &&
    props.createdAt <= Date.now() / 1000
);
const acceptedReaction = computed(() => {
  const reaction = props.contentAttributes.whatsappReaction;
  return isPublicIncoming.value &&
    reaction?.status === 'accepted' &&
    emojis.includes(reaction?.emoji)
    ? reaction
    : null;
});

async function sendReaction(emoji) {
  if (pending.value || !canReact.value) return;
  pending.value = true;
  error.value = '';
  noteFailed.value = false;
  try {
    const result = await store.dispatch('sendMessageReaction', {
      conversationId: props.conversationId,
      messageId: props.messageId,
      emoji,
    });
    noteFailed.value = result.note_status === 'failed';
  } catch (err) {
    error.value =
      err.response?.data?.error || t('CONVERSATION.WHATSAPP_REACTION.ERROR');
  } finally {
    pending.value = false;
  }
}
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="canReact || acceptedReaction"
    class="flex flex-col items-start gap-1 mt-1"
    @contextmenu.stop
  >
    <MessageReactionBadge
      v-if="acceptedReaction"
      :reaction="acceptedReaction"
    />
    <MessageReactionPicker
      v-if="canReact"
      :pending="pending"
      :accepted-emoji="acceptedReaction?.emoji || ''"
      @select="sendReaction"
    />
    <span v-if="error" role="alert" class="text-xs text-n-ruby-11">
      {{ error }}
    </span>
    <span
      v-if="contentAttributes.whatsappReaction?.status === 'failed'"
      role="alert"
      class="text-xs text-n-ruby-11"
    >
      {{ t('CONVERSATION.WHATSAPP_REACTION.DELIVERY_FAILED') }}
    </span>
    <span
      v-if="noteFailed || acceptedReaction?.noteStatus === 'failed'"
      role="alert"
      class="text-xs text-n-amber-11"
    >
      {{ t('CONVERSATION.WHATSAPP_REACTION.NOTE_FAILED') }}
    </span>
  </div>
</template>
